import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DomSanitizer} from '@angular/platform-browser';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {SidebarService} from '../../services/sidebar.service';
import {Subscription} from 'rxjs';
import {MatIcon, MatIconRegistry} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {AuthenticationService} from '@core/auth/authentication.service';

type NavItem = {
  name: string;
  icon: string;
  iconType?: 'material' | 'svg';
  /** Módulo de permisos: sin acceso, el ítem no se muestra. */
  module: string;
  path: string;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

/**
 * Menú lateral: todos los destinos a la vista y a un clic, agrupados por uso.
 * Primero el trabajo de todos los días; la configuración, al final.
 *
 * 2026-09-23: antes cada módulo era un acordeón (dos clics por destino, uno
 * solo abierto a la vez y grupos de una sola opción).
 */
@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    MatIcon,
    MatTooltip
  ],
  templateUrl: './app-sidebar.html'
})
export class AppSidebar implements OnInit, OnDestroy {

  private allSections: NavSection[] = [
    {
      title: 'Trabajo diario',
      items: [
        { name: 'Calendario', icon: 'event', module: 'agendamientos', path: '/agendamientos' },
        // Oculto a pedido (2026-09-23); la página sigue en /laboratorio.
        // { name: 'Panel del laboratorio', icon: 'science', module: 'laboratorio', path: '/laboratorio' },
        // 2026-09-24: OPU se combinó con PIVE; las aspiraciones son su primer paso.
        // { name: 'OPU', icon: 'colorize', module: 'laboratorio', path: '/pive' },
        // 2026-10-08: 'laboratorio' se dividió en PIVE, TE y Stock, cada uno con su permiso.
        { name: 'PIVE', icon: 'biotech', module: 'pive', path: '/pive' },
        { name: 'Stock', icon: 'inventory_2', module: 'stock', path: '/stock' },
        // 2026-09-24: la TE se agenda en el calendario; la planilla (receptoras, ejecución, diagnóstico) vive acá.
        { name: 'TE', icon: 'sync_alt', module: 'te', path: '/te' },
        { name: 'Gestión de animales', icon: 'cow', iconType: 'svg', module: 'animales', path: '/animales' },
      ],
    },
    {
      title: 'Clientes',
      items: [
        { name: 'Clientes', icon: 'groups', module: 'clientes', path: '/crm/clientes' },
        { name: 'Prospectos', icon: 'person_search', module: 'prospectos', path: '/crm/prospectos' },
        // { name: 'Establecimientos', icon: 'agriculture', module: 'zonas', path: '/ubicacion/establecimientos' },
        { name: 'Zonas', icon: 'map', module: 'zonas', path: '/ubicacion/zonas' },
      ],
    },
    {
      title: 'Configuración',
      items: [
        { name: 'Paramétricos', icon: 'tune', module: 'parametricos', path: '/parametricos' },
        { name: 'Usuarios', icon: 'manage_accounts', module: 'usuarios', path: '/administracion/usuarios' },
        { name: 'Roles', icon: 'admin_panel_settings', module: 'roles', path: '/administracion/roles' },
      ],
    },
  ];

  /** Secciones con los ítems que el usuario puede ver; las vacías no se muestran. */
  sections = computed(() => {
    const puedeVer = (item: NavItem) => this.authService.isSuperAdmin() || this.authService.canAccessModule(item.module);
    return this.allSections
      .map(section => ({ ...section, items: section.items.filter(puedeVer) }))
      .filter(section => section.items.length > 0);
  });

  /** Ítem de la página actual: el de ruta más larga que la contiene (el detalle de un OPU marca PIVE). */
  activePath = signal<string | null>(null);

  public sidebarService = inject(SidebarService);
  private router = inject(Router);
  private subscription: Subscription = new Subscription();
  private authService = inject(AuthenticationService);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);

  constructor() {
    this.iconRegistry.addSvgIcon(
      'cow',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/icons/cow.svg'),
    );
  }

  readonly isExpanded$ = this.sidebarService.isExpanded$;
  readonly isMobileOpen$ = this.sidebarService.isMobileOpen$;

  ngOnInit() {
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.updateActive(event.urlAfterRedirects);
        }
      })
    );
    this.updateActive(this.router.url);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    return this.activePath() === path;
  }

  private updateActive(url: string) {
    const ruta = url.split(/[?#]/)[0];
    const candidatos = this.allSections
      .flatMap(section => section.items.map(item => item.path))
      .filter(path => ruta === path || ruta.startsWith(path + '/'))
      .sort((a, b) => b.length - a.length);
    this.activePath.set(candidatos[0] ?? null);
  }

  /** En el celular, elegir un destino cierra el menú. */
  onItemClick() {
    this.isMobileOpen$.subscribe(isMobile => {
      if (isMobile) {
        this.sidebarService.setMobileOpen(false);
      }
    }).unsubscribe();
  }
}
